import { useEffect, useState } from 'react';
import api from '../../../utils/api';

// Accessibility for the test chat: two preferences from the chat header (A11yMenu.js), remembered
// on this device, and Anna's voice for reading questions aloud (Gemini on the server,
// custom_auth/speech_views.py). Only a question's wording is ever sent to be read.

const KEY = 'anna:a11y';
const EVENT = 'anna:a11y-change';
const DEFAULTS = { largeText: false, readAloud: false };

const readPrefs = () => {
  try {
    return { ...DEFAULTS, ...(JSON.parse(window.localStorage.getItem(KEY)) || {}) };
  } catch (error) {
    return { ...DEFAULTS };
  }
};

/** [prefs, update]: { largeText, readAloud }, kept in step across the header and the test. */
export const useA11yPrefs = () => {
  const [prefs, setPrefs] = useState(readPrefs);
  useEffect(() => {
    const sync = () => setPrefs(readPrefs());
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  const update = (patch) => {
    const next = { ...readPrefs(), ...patch };
    try {
      window.localStorage.setItem(KEY, JSON.stringify(next));
    } catch (error) {
      // Private mode: the choice still applies until the page closes
    }
    setPrefs(next);
    window.dispatchEvent(new Event(EVENT));
  };
  return [prefs, update];
};

let statusRequest = null;
/** Whether this server can read aloud (asked once per page load). */
export const speechStatus = () => {
  if (!statusRequest) {
    statusRequest = api
      .get('/api/speech/status/')
      .then((response) => Boolean(response.data && response.data.available))
      .catch(() => {
        statusRequest = null;
        return false;
      });
  }
  return statusRequest;
};

export const useSpeechAvailable = () => {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    let live = true;
    speechStatus().then((value) => {
      if (live) setAvailable(value);
    });
    return () => {
      live = false;
    };
  }, []);
  return available;
};

const clips = new Map();
/** A playable URL for the question read in Anna's voice; each wording is fetched once per visit. */
export const loadClip = (text, lang) => {
  const key = `${lang}|${text}`;
  if (!clips.has(key)) {
    const request = api
      .post('/api/speech/', { text, language: lang }, { responseType: 'blob', timeout: 60000 })
      .then((response) => URL.createObjectURL(response.data))
      .catch((error) => {
        clips.delete(key);
        throw error;
      });
    clips.set(key, request);
  }
  return clips.get(key);
};
